import { DateField } from "../features/insights/components";
import { Field } from "./ui";

export type DeleteWhen = "all" | "range";

/** First day a date-range delete can start, through today. */
export function deleteDateBounds(now = new Date()): { min: string; max: string } {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return { min: "2000-01-01", max: `${now.getFullYear()}-${month}-${day}` };
}

function dayLabel(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year!, month! - 1, day).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** The words that follow "every session" or "requests and logs". Empty until a range is complete. */
export function deleteWhenPhrase(mode: DeleteWhen, from: string, to: string): string {
  if (mode === "all") return "from the beginning";
  if (!from || !to) return "";
  if (from > to) return "";
  return `from ${dayLabel(from)} through ${dayLabel(to)}`;
}

/** ISO bounds for the API. Null until the choice is complete and in order. */
export function deleteWhenBounds(mode: DeleteWhen, from: string, to: string): { from?: string; to?: string } | null {
  if (mode === "all") return {};
  if (!from || !to || from > to) return null;
  const [fy, fm, fd] = from.split("-").map(Number);
  const [ty, tm, td] = to.split("-").map(Number);
  const start = new Date(fy!, fm! - 1, fd);
  const end = new Date(ty!, tm! - 1, td! + 1);
  return { from: start.toISOString(), to: end.toISOString() };
}

/** From the beginning, or the first and last day. */
export function DeleteWhenFields({
  mode,
  from,
  to,
  onMode,
  onFrom,
  onTo,
}: {
  mode: DeleteWhen;
  from: string;
  to: string;
  onMode: (mode: DeleteWhen) => void;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
}) {
  const bounds = deleteDateBounds();
  const orderError = mode === "range" && from && to && from > to ? "The start is after the end." : undefined;
  return (
    <div className="delete-when">
      <fieldset className="delete-when-choice">
        <legend>Time range</legend>
        <label className="delete-when-option">
          <input type="radio" name="delete-when" checked={mode === "all"} onChange={() => onMode("all")} />
          <span>From the beginning</span>
        </label>
        <label className="delete-when-option">
          <input type="radio" name="delete-when" checked={mode === "range"} onChange={() => onMode("range")} />
          <span>Date range</span>
        </label>
      </fieldset>
      {mode === "range" && (
        <div className="field-row delete-when-dates">
          <Field label="First day">
            {(p) => (
              <DateField id={p.id} label="First day" className="input" committed={from} min={bounds.min} max={to && to < bounds.max ? to : bounds.max} onCommit={onFrom} />
            )}
          </Field>
          <Field label="Last day" error={orderError}>
            {(p) => (
              <DateField id={p.id} label="Last day" className="input" committed={to} min={from && from > bounds.min ? from : bounds.min} max={bounds.max} onCommit={onTo} />
            )}
          </Field>
        </div>
      )}
    </div>
  );
}
